import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Search } from "lucide-react";
import { BrandWordmark } from "@/components/BrandWordmark";
import { Helmet } from "react-helmet-async";
import Seo from "@/components/Seo";
import { CreateathonBanner } from "@/components/CreateathonBanner";

const FAQ_SECTIONS = [
  {
    title: "Getting started",
    items: [
      {
        q: "What is Videoileo?",
        a: "Videoileo is a faceless video studio. Type a topic and AI writes the script, records a studio-grade voiceover and generates cinematic visuals — then you download, publish to YouTube or schedule a whole series. No camera, no editing skills needed.",
      },
      {
        q: "Is the free trial really free?",
        a: "Yes — sign up with just your email (or Google) and you get 100 free credits, enough to make a couple of full short videos end-to-end. No card required.",
      },
      {
        q: "Do I need to show my face or record anything?",
        a: "Never. Scripts, voiceovers, visuals, music and motion are all generated for you. That's the whole point of a faceless channel.",
      },
      {
        q: "Can I sign in with Google?",
        a: "Yes — use the \"Continue with Google\" button on the sign-in or sign-up page. Google accounts share the same credits and features as email accounts.",
      },
    ],
  },
  {
    title: "Credits & plans",
    items: [
      {
        q: "What's a credit?",
        a: "Credits power AI generation — scripts, studio voiceovers and cinematic scene visuals. Every paid plan includes a monthly credit allotment, and you can buy one-time top-up packs anytime.",
      },
      {
        q: "How many credits does a video cost?",
        a: "A script is 10 credits, each scene visual is 5, and a voiceover is 5 (OpenAI voices) or 10 (ElevenLabs). A typical 3-scene short costs around 30 credits; a 7-scene long-form around 55. Optional extras: AI motion clips are 25 (short scenes) or 40 (long scenes) per scene — subscribers save 15% when animating all scenes in one batch — and YouTube thumbnails are 5.",
      },
      {
        q: "Can I change or cancel my plan?",
        a: "Plans are month-to-month. Upgrade, downgrade, or cancel anytime — you keep any credits already in your balance.",
      },
      {
        q: "Can I earn free credits?",
        a: "Yes — refer friends and earn 100 credits for each one who joins (up to 5). Your referral link is in the Refer & Earn tab.",
      },
    ],
  },
  {
    title: "Creating videos",
    items: [
      {
        q: "What visual styles are available?",
        a: "Comic book, anime, minimal, cinematic and more — pick a style per video and restyle any time. You can also add true AI 3D motion to any scene, or animate all scenes in one click.",
      },
      {
        q: "Can I edit the script and scenes?",
        a: "Yes — every scene's narration, on-screen caption, transition and duration are editable. You can reorder, delete and regenerate visuals per scene before building the final video.",
      },
      {
        q: "How long does rendering take?",
        a: "Most shorts render in under a minute. Long-form videos with music, motion clips and crossfades typically take a few minutes — you can leave the page and come back.",
      },
      {
        q: "Can I make long-form videos?",
        a: "Yes — choose Long when creating and you'll get a 7-scene script built for watch time, with background music and scene transitions.",
      },
    ],
  },
  {
    title: "Publishing & scheduling",
    items: [
      {
        q: "Does Videoileo post to social automatically?",
        a: "You can schedule a whole series to a YouTube, TikTok & Instagram queue today. Connected YouTube channels get automatic uploads; fully automated publishing to the other platforms is rolling out.",
      },
      {
        q: "How do I connect my YouTube channel?",
        a: "Go to Settings → Connected channels and click Connect YouTube. Once connected, you can publish any finished video straight to your channel — or let a scheduled series post for you.",
      },
      {
        q: "What is a Series?",
        a: "A Series is a batch of videos on one theme, generated and scheduled automatically — the fastest way to grow subscribers with consistent posting.",
      },
      {
        q: "Do I own the videos I create?",
        a: "Yes — download your MP4s and use them anywhere. See our Content & Copyright Policy for the fine print.",
      },
    ],
  },
  {
    title: "Account & security",
    items: [
      {
        q: "How do I change or reset my password?",
        a: "Logged in? Head to Settings → Change password. Forgot it? Use the \"Forgot password?\" link on the sign-in page and we'll email you a secure reset link that expires in 60 minutes. Google sign-in users can use the same link to set a password too.",
      },
      {
        q: "Will I be notified if my password changes?",
        a: "Yes — every password change triggers an instant security alert email to your account address, so you can react immediately if it wasn't you.",
      },
      {
        q: "Can I sign out of all devices?",
        a: "Yes — when changing your password in Settings, tick \"Also sign out of all other devices\" and every other session is ended instantly. Resetting via email link signs out all devices automatically.",
      },
      {
        q: "Can I delete my account?",
        a: "Yes — Settings → Delete account permanently removes your account and everything in it (videos, voiceovers, visuals, series and scheduled posts).",
      },
    ],
  },
];

export default function Faq() {
  const [query, setQuery] = useState("");
  const allItems = FAQ_SECTIONS.flatMap((s) => s.items);
  const visibleSections = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return FAQ_SECTIONS;
    return FAQ_SECTIONS
      .map((s) => ({ ...s, items: s.items.filter((i) => i.q.toLowerCase().includes(q) || i.a.toLowerCase().includes(q)) }))
      .filter((s) => s.items.length > 0);
  }, [query]);
  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white" data-testid="faq-page">
      <CreateathonBanner />
      <Seo
        title="FAQ — Frequently Asked Questions"
        description="Answers to common questions about Videoileo: free trial, credits, AI video creation, YouTube scheduling, account security and more."
        path="/faq"
      />
      <Helmet>
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: allItems.map((f) => ({
              "@type": "Question",
              name: f.q,
              acceptedAnswer: { "@type": "Answer", text: f.a },
            })),
          })}
        </script>
      </Helmet>

      <header className="border-b border-white/10">
        <div className="max-w-4xl mx-auto px-5 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center">
            <BrandWordmark className="h-12" />
          </Link>
          <div className="flex items-center gap-3">
            <Link to="/login" className="text-sm text-zinc-300 hover:text-white px-3 py-2 transition-colors">Sign in</Link>
            <Link to="/register" data-testid="faq-start-free" className="text-sm bg-blue-600 hover:bg-blue-500 px-4 py-2 rounded-md font-medium transition-colors">
              Start free
            </Link>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-5 py-14">
        <p className="text-xs uppercase tracking-[0.2em] text-zinc-500 mb-3">FAQ</p>
        <h1 className="font-head text-4xl sm:text-5xl tracking-tighter font-black mb-4">Frequently asked questions</h1>
        <p className="text-zinc-400 max-w-xl mb-8 leading-relaxed">
          Everything about credits, creating faceless videos, publishing to YouTube and keeping your account safe.
        </p>

        <div className="relative max-w-md mb-12">
          <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-zinc-500 pointer-events-none" />
          <input
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search questions… e.g. credits, password, YouTube"
            data-testid="faq-search-input"
            className="w-full bg-[#121212] border border-white/10 rounded-md pl-10 pr-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-shadow placeholder:text-zinc-600"
          />
        </div>

        <div className="space-y-12">
          {visibleSections.map((section) => (
            <section key={section.title} data-testid={`faq-section-${section.title.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
              <h2 className="font-head text-lg font-bold mb-4 text-blue-400">{section.title}</h2>
              <div className="space-y-4">
                {section.items.map((item) => (
                  <div key={item.q} className="border border-white/10 rounded-md bg-[#121212] p-5">
                    <h3 className="font-head font-semibold mb-1.5">{item.q}</h3>
                    <p className="text-sm text-zinc-400 leading-relaxed">{item.a}</p>
                  </div>
                ))}
              </div>
            </section>
          ))}
          {visibleSections.length === 0 && (
            <div className="border border-white/10 rounded-md bg-[#121212] p-8 text-center" data-testid="faq-no-results">
              <p className="text-sm text-zinc-400 mb-1 font-semibold">No matches for "{query}"</p>
              <p className="text-sm text-zinc-500">
                Try a different word — or <Link to="/contact" className="text-blue-400 hover:text-blue-300 underline">ask us directly</Link>.
              </p>
            </div>
          )}
        </div>

        <p className="text-sm text-zinc-500 mt-12 leading-relaxed" data-testid="faq-more-links">
          Still stuck? <Link to="/contact" className="text-blue-400 hover:text-blue-300 underline">Contact us</Link> — or dive into the{" "}
          <Link to="/guides" className="text-blue-400 hover:text-blue-300 underline">free faceless video guides</Link> and{" "}
          <Link to="/pricing" className="text-blue-400 hover:text-blue-300 underline">pricing plans</Link>.
        </p>
      </main>

      <footer className="border-t border-white/10 py-10 px-5 text-center text-sm text-zinc-600">
        © 2026 Videoileo · The faceless video studio.
        <span className="mx-2">·</span>
        <Link to="/" className="text-zinc-500 hover:text-zinc-300 underline">Home</Link>
        <span className="mx-2">·</span>
        <Link to="/pricing" className="text-zinc-500 hover:text-zinc-300 underline">Pricing</Link>
      </footer>
    </div>
  );
}
